import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

import ShellLogoutButton from '@/components/app-shell/ShellLogoutButton';
import ShellSidebarNavItem from '@/components/app-shell/ShellSidebarNavItem';
import ShellUserCard from '@/components/app-shell/ShellUserCard';
import AstroImage from '@/components/ui/custom/AstroImage';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy/button';
import { Menu } from '@/components/ui/daisy/menu';
import { Swap } from '@/components/ui/daisy/swap';
import { useAppShell } from '@/data/context/AppShellContext';
import useShellSidebar from '@/data/hook/app-shell/useShellSidebar';
import { isStudent } from '@/functions/auth';

export default function ShellSidebar() {
  const { user, navItems } = useAppShell();
  const { isOpen, toggle, close } = useShellSidebar();
  const { t } = useTranslation('translation', { keyPrefix: 'appShell' });

  return (
    <div className="relative h-full w-19">
      <aside
        className={twJoin(
          'absolute inset-y-0 left-0 flex flex-col justify-between backdrop-blur-[8px] transition-[width] duration-150 motion-reduce:transition-none',
          isStudent(user.userType) ? 'bg-base-200/90' : 'bg-base-100/90',
          isOpen ? 'w-68 px-6 py-4.5' : 'w-19 px-3.25 py-6',
        )}
      >
        <div className="flex flex-col gap-6">
          <div
            className={twJoin(
              'flex h-16 items-center overflow-hidden py-4',
              isOpen ? 'justify-between' : 'justify-center',
            )}
          >
            <ConditionalRenderer condition={isOpen}>
              <AstroImage
                src="/images/cna-logo.svg"
                alt="CNA"
                className="h-7.5 w-28 shrink-0"
              />
            </ConditionalRenderer>
            <Button
              variant="ghost"
              shape="circle"
              aria-expanded={isOpen}
              aria-label={t(isOpen ? 'sidebar.collapse' : 'sidebar.expand')}
              onClick={toggle}
            >
              <Swap effect="rotate" active={isOpen}>
                <Swap.Off>
                  <Bars3Icon className="size-8" />
                </Swap.Off>
                <Swap.On>
                  <XMarkIcon className="size-8" />
                </Swap.On>
              </Swap>
            </Button>
          </div>
          <nav aria-label={t('nav.label')}>
            <Menu className="w-full gap-6 p-0">
              {navItems.map(({ type, href, activePath, icon, labelKey }) => (
                <ShellSidebarNavItem
                  key={type}
                  href={href}
                  activePath={activePath}
                  icon={icon}
                  label={t(`nav.${labelKey}`)}
                  isOpen={isOpen}
                  onNavigate={close}
                />
              ))}
            </Menu>
          </nav>
        </div>
        <div className="flex flex-col items-center gap-6">
          <ShellUserCard isOpen={isOpen} onNavigate={close} />
          <ShellLogoutButton variant={isOpen ? 'expanded' : 'compact'} />
        </div>
      </aside>
    </div>
  );
}
